import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  Bomb, Coins, Flag, FlaskConical, LayoutDashboard, Loader2, Pause, Play, Plus,
  ScrollText, SlidersHorizontal, Trophy, Users, Wallet,
} from 'lucide-react';
import WbAdminConfig from './wb/WbAdminConfig';
import WbAdminPrizes from './wb/WbAdminPrizes';
import WbAdminTestAccounts from './wb/WbAdminTestAccounts';
import { WbRecharges, WbPlayers, WbPops, WbRewards, WbLogs } from './wb/WbAdminTables';

const SECTIONS = [
  { id: 'dashboard', label: 'Visão geral', icon: LayoutDashboard },
  { id: 'config', label: 'Economia', icon: SlidersHorizontal },
  { id: 'recharges', label: 'Recargas', icon: Wallet },
  { id: 'players', label: 'Bruxos (jogadores)', icon: Users },
  { id: 'pops', label: 'Balões estourados', icon: Bomb },
  { id: 'rewards', label: 'Prêmios (entrega KAKO)', icon: Coins },
  { id: 'prizes', label: 'Ranking & prêmios finais', icon: Trophy },
  { id: 'tests', label: 'Contas de teste', icon: FlaskConical },
  { id: 'logs', label: 'Auditoria', icon: ScrollText },
];
const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');
/* sem fuso no texto = UTC (Mongo); sempre exibe no horário do Brasil */
const dt = (iso) => { try { return new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(String(iso)) ? iso : `${iso}Z`).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch (e) { return '-'; } };
const left = (s) => { s = Math.max(0, s || 0); const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60); return d ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`; };
const toIso = (local) => (local ? new Date(local).toISOString() : null);
const Stat = ({ l, v, cls = '', testid }) => (
  <div className="rounded-xl border p-3"><p className="text-[10px] uppercase text-slate-400">{l}</p><p className={`font-black ${cls}`} data-testid={testid}>{v}</p></div>
);

const CreateEvent = ({ onCreated }) => {
  const local = (d) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 16); };
  const fimPadrao = new Date(); fimPadrao.setDate(fimPadrao.getDate() + 14); fimPadrao.setHours(23, 59, 0, 0);
  const [ini, setIni] = useState(local(new Date()));
  const [fim, setFim] = useState(local(fimPadrao));
  const [nome, setNome] = useState("WITCH'S BALLOON");
  const [busy, setBusy] = useState(false);
  const s = new Date(ini); const e = new Date(fim);
  const dias = Math.max(0, Math.round((e - s) / 86400000 * 10) / 10);
  const agendado = s.getTime() > Date.now() + 60000;
  const criar = async () => {
    if (!(e > s)) { toast.error('O fim precisa ser depois do início.'); return; }
    setBusy(true);
    try {
      await api.post('/admin/wb/event', { name: nome, starts_at: s.toISOString(), ends_at: e.toISOString() });
      toast.success(agendado ? 'Evento agendado! Começa e termina sozinho nas datas marcadas.' : 'Evento criado e NO AR! Termina sozinho na data marcada.'); onCreated();
    } catch (er) { toast.error(er.response?.data?.detail || 'Erro ao criar.'); }
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid="wb-admin-create">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs font-bold text-slate-600">Nome<input value={nome} onChange={(ev) => setNome(ev.target.value)} className="mt-1 block h-9 w-56 rounded-lg border px-2 text-sm" data-testid="wb-admin-create-name" /></label>
        <label className="text-xs font-bold text-slate-600">Início<input type="datetime-local" value={ini} onChange={(ev) => setIni(ev.target.value)} className="mt-1 block h-9 rounded-lg border px-2 text-sm" data-testid="wb-admin-create-start" /></label>
        <label className="text-xs font-bold text-slate-600">Fim<input type="datetime-local" value={fim} onChange={(ev) => setFim(ev.target.value)} className="mt-1 block h-9 rounded-lg border px-2 text-sm" data-testid="wb-admin-create-end" /></label>
        <Button onClick={criar} disabled={busy} className="bg-orange-500 text-white hover:bg-orange-600" data-testid="wb-admin-create-btn"><Plus className="mr-1 h-4 w-4" /> {agendado ? 'Agendar evento' : 'Criar evento (no ar agora)'}</Button>
      </div>
      <p className="text-[11px] text-slate-500" data-testid="wb-admin-create-hint">
        {e > s ? `${dias} dia(s) de evento · ` : 'Fim precisa ser depois do início · '}
        {agendado ? `começa sozinho em ${dt(s.toISOString())}` : 'começa agora'} e <b>encerra sozinho em {dt(e.toISOString())}</b> (horário de Brasília).
      </p>
    </div>
  );
};

export default function WitchBalloonManagement() {
  const [ov, setOv] = useState(null);
  const [section, setSection] = useState('dashboard');
  const [busy, setBusy] = useState(false);
  const [wKako, setWKako] = useState('');
  const [wAmt, setWAmt] = useState(0);
  const [wNote, setWNote] = useState('');
  const [endIn, setEndIn] = useState('');
  const [startIn, setStartIn] = useState('');
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try { const r = await api.get('/admin/wb/overview'); setOv(r.data); }
    catch { toast.error("Erro ao carregar o WITCH'S BALLOON."); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const act = async (fn, ok, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); setTick((t) => t + 1); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro na operação.'); }
    setBusy(false);
  };

  if (!ov) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>;
  const ev = ov.event;
  const ended = ev?.status === 'ENDED';

  return (
    <div className="space-y-4" data-testid="wb-admin">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg"><img src="/wb/balloon.webp" alt="" className="h-6 w-6" /> WITCH'S BALLOON — evento programado por datas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(!ev || ended) && <CreateEvent onCreated={load} />}
          {ev && (
            <>
              {ov.seconds_to_start > 0 && !ended && (
                <p className="rounded-xl border border-sky-300 bg-sky-50 p-2 text-xs font-bold text-sky-800" data-testid="wb-admin-scheduled">
                  🗓️ Agendado: começa sozinho em {dt(ev.starts_at)} (faltam {left(ov.seconds_to_start)}) e encerra sozinho em {dt(ev.ends_at)}.
                </p>
              )}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat l="Status" v={ended ? 'ENCERRADO' : ov.seconds_to_start > 0 ? 'AGENDADO' : `${ev.status}${ov.live ? ' · NO AR' : ''}`} cls={ov.live ? 'text-emerald-600' : ov.seconds_to_start > 0 ? 'text-sky-700' : 'text-slate-700'} testid="wb-admin-status" />
                <Stat l="Dia / resta" v={ov.seconds_to_start > 0 ? `começa em ${left(ov.seconds_to_start)}` : `${ov.day_index}/${ev.config?.duration_days} · ${left(ov.seconds_left)}`} />
                <Stat l="Recargas · moedas" v={`${fmt(ev.totals?.recharges)} · ${fmt(ev.totals?.coins)}`} />
                <Stat l="Moedas da Bruxa emitidas · em carteira" v={`${fmt(ev.totals?.witch_coins)} · ${fmt(ov.witch_coins_outstanding)}`} cls="text-violet-600" testid="wb-admin-witch" />
                <Stat l="Balões estourados (hoje)" v={`${fmt(ev.totals?.pops)} (${fmt(ov.pops_today)})`} cls="text-orange-600" testid="wb-admin-pops" />
                <Stat l="Prêmios pagos (1.000 · 50.000)" v={`${fmt(ev.totals?.wins_small)} · ${fmt(ev.totals?.wins_big)} = ${fmt(ev.totals?.prizes_coins)} moedas`} cls="text-amber-600" testid="wb-admin-prizes" />
                <Stat l="Fundo: prêmios 1.000 · dourado" v={`${fmt(ov.fund?.campaign?.regular_pool)} · ${fmt(ov.fund?.campaign?.jackpot_pool)} ${ov.fund?.big_active ? '(dourado NO AR)' : '(dourado sem lastro)'}`} cls={ov.fund?.big_active ? 'text-emerald-600' : 'text-slate-600'} testid="wb-admin-fund" />
                <Stat l="Jogadores · entregas pendentes" v={`${fmt(ov.participants)} · ${fmt(ov.rewards_pending)}${ov.rewards_review ? ` · ${ov.rewards_review} em revisão` : ''}`} testid="wb-admin-rewards" />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-slate-50 px-3 py-2" data-testid="wb-visibility-row">
                <div>
                  <p className="text-xs font-black uppercase text-slate-700">Mostrar evento para o público</p>
                  <p className="text-[11px] text-slate-500">
                    {ov.public_visible
                      ? (ended ? 'Ligado: evento encerrado aparece no site em modo consulta (ranking final e histórico).' : 'Ligado: o evento aparece no site normalmente.')
                      : 'Desligado: some da Conta do Cliente e quem entrar pelo link vê "EVENTO INATIVO" — encerrado ou não.'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-black ${ov.public_visible ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="wb-visibility-label">{ov.public_visible ? 'VISÍVEL' : 'OCULTO'}</span>
                  <Switch checked={!!ov.public_visible} disabled={busy} data-testid="wb-visibility-switch"
                    onCheckedChange={(v) => act(() => api.patch('/admin/wb/visibility', { public_visible: v }), v ? 'Evento visível para o público.' : 'Evento escondido do público.')} />
                </div>
              </div>

              {!ended && (
                <div className="space-y-3 rounded-xl border border-orange-200 bg-orange-50/50 p-3" data-testid="wb-admin-controls">
                  <p className="text-xs font-black uppercase tracking-wider text-orange-700">Controles ao vivo (tudo auditado)</p>
                  <div className="flex flex-wrap gap-2">
                    {ev.status === 'ACTIVE'
                      ? <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => api.post('/admin/wb/status', { status: 'PAUSED' }), 'Evento pausado.')} data-testid="wb-admin-pause"><Pause className="mr-1 h-4 w-4" /> Pausar</Button>
                      : <Button size="sm" disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => act(() => api.post('/admin/wb/status', { status: 'ACTIVE' }), 'Evento retomado!')} data-testid="wb-admin-resume"><Play className="mr-1 h-4 w-4" /> Retomar</Button>}
                    <Button size="sm" variant="destructive" disabled={busy} data-testid="wb-admin-end"
                      onClick={() => act(() => api.post('/admin/wb/end'), 'Evento encerrado. Prêmios aguardando aprovação.', 'ENCERRAR o evento? Ninguém mais estoura balões, o ranking congela e os prêmios finais ficam aguardando sua aprovação. Irreversível!')}><Flag className="mr-1 h-4 w-4" /> Encerrar evento</Button>
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <label className="text-xs font-bold text-slate-600"><Wallet className="mr-1 inline h-3.5 w-3.5" />Ajustar Moedas da Bruxa de um jogador
                      <div className="mt-1 flex gap-1"><input value={wKako} onChange={(e) => setWKako(e.target.value)} placeholder="KAKO ID" className="h-9 w-28 rounded-lg border px-2 text-sm" data-testid="wb-admin-wallet-kako" />
                        <input type="number" value={wAmt} onChange={(e) => setWAmt(+e.target.value)} placeholder="± qtd" className="h-9 w-24 rounded-lg border px-2 text-sm" data-testid="wb-admin-wallet-amount" />
                        <input value={wNote} onChange={(e) => setWNote(e.target.value)} placeholder="motivo" className="h-9 w-32 rounded-lg border px-2 text-sm" data-testid="wb-admin-wallet-note" />
                        <Button size="sm" variant="outline" disabled={busy || !wKako || !wAmt} onClick={() => act(() => api.post('/admin/wb/wallet/adjust', { kako_id: wKako.trim(), amount: wAmt, note: wNote }), 'Carteira ajustada.', `Aplicar ${wAmt > 0 ? '+' : ''}${fmt(wAmt)} Moedas da Bruxa em ${wKako}?`)} data-testid="wb-admin-wallet-btn">Aplicar</Button></div>
                    </label>
                    {ov.seconds_to_start > 0 && (
                      <label className="text-xs font-bold text-slate-600">Novo início do evento
                        <div className="mt-1 flex gap-1"><input type="datetime-local" value={startIn} onChange={(e) => setStartIn(e.target.value)} className="h-9 rounded-lg border px-2 text-sm" data-testid="wb-admin-start-input" />
                          <Button size="sm" variant="outline" disabled={busy || !startIn} onClick={() => act(() => api.put('/admin/wb/dates', { starts_at: toIso(startIn) }), 'Início alterado.')} data-testid="wb-admin-start-btn">Aplicar</Button></div>
                      </label>
                    )}
                    <label className="text-xs font-bold text-slate-600">Novo fim do evento (encerra sozinho)
                      <div className="mt-1 flex gap-1"><input type="datetime-local" value={endIn} onChange={(e) => setEndIn(e.target.value)} className="h-9 rounded-lg border px-2 text-sm" data-testid="wb-admin-end-input" />
                        <Button size="sm" variant="outline" disabled={busy || !endIn} onClick={() => act(() => api.put('/admin/wb/dates', { ends_at: toIso(endIn) }), 'Data alterada.')} data-testid="wb-admin-end-btn">Aplicar</Button></div>
                    </label>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {ev && (
        <Card data-testid="wb-admin-panel">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap gap-1">
              {SECTIONS.map((s) => (
                <button key={s.id} onClick={() => setSection(s.id)} data-testid={`wb-admin-tab-${s.id}`}
                  className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${section === s.id ? 'border-orange-400 bg-orange-50 text-orange-700' : 'text-slate-500 hover:bg-slate-50'}`}>
                  <s.icon className="h-3.5 w-3.5" /> {s.label}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {section === 'dashboard' && (
              <div className="space-y-2 text-xs text-slate-600" data-testid="wb-admin-dashboard">
                <p><b>Como funciona (modelo das Azulzinhas):</b> cada recarga paga rende <b>{(ev.config?.witch_coins?.rate_bp || 0) / 100}%</b> em Moedas da Bruxa ao cliente (100.000 → {fmt(100000 * (ev.config?.witch_coins?.rate_bp || 0) / 10000)}) e <b>{(ev.config?.fund?.contribution_bp || 0) / 100}%</b> vai para o fundo do evento, dividido {ev.config?.fund?.splits?.regular_pool}% prêmios de {fmt(ev.config?.pop?.prize_small)} / {ev.config?.fund?.splits?.jackpot_pool}% balão dourado de {fmt(ev.config?.pop?.prize_big)}.</p>
                <p><b>Estourar:</b> custa {fmt(ev.config?.witch_coins?.pop_cost)} Moedas da Bruxa. Chances base: {(ev.config?.pop?.prob_small_ppm || 0) / 10000}% prêmio pequeno · {(ev.config?.pop?.prob_big_ppm || 0) / 10000}% dourado (sobem quando o fundo acumula). Prêmio só sai com lastro no fundo; limite de {ev.config?.pop?.big_daily_cap} dourados por pessoa/dia.</p>
                <p><b>Ranking:</b> por balões estourados · prêmios fixos {(ev.config?.ranking?.prizes || []).map((p, i) => `${i + 1}º ${fmt(p)}`).join(' · ')} moedas, liberados por você após o encerramento. Contas de teste ficam fora.</p>
                <p><b>Fundo agora:</b> {fmt(ov.fund?.campaign?.regular_pool)} (prêmios pequenos) · {fmt(ov.fund?.campaign?.jackpot_pool)} (dourado) · {fmt(ov.fund?.campaign?.lifetime_prizes_settled)} já pagos em prêmios.</p>
              </div>
            )}
            {section === 'config' && <WbAdminConfig onSaved={load} />}
            {section === 'recharges' && <WbRecharges reloadKey={tick} />}
            {section === 'players' && <WbPlayers reloadKey={tick} />}
            {section === 'pops' && <WbPops reloadKey={tick} />}
            {section === 'rewards' && <WbRewards reloadKey={tick} />}
            {section === 'prizes' && <WbAdminPrizes ended={ended} />}
            {section === 'tests' && <WbAdminTestAccounts />}
            {section === 'logs' && <WbLogs reloadKey={tick} />}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
